import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatedSection } from "@/components/AnimatedSection";
import { ArrowRight, Target, TrendingUp, Wallet, Shield, Building2, Users } from "lucide-react";

const services = [
  {
    icon: Target,
    title: "Planejamento Financeiro",
    description: "A base de todas as decisões. Construímos um plano claro, viável e alinhado aos seus objetivos de vida e liberdade financeira.",
  },
  {
    icon: TrendingUp,
    title: "Consultoria de Investimentos",
    description: "Alocação estratégica conectada ao seu plano, sem conflito de interesses e sem decisões isoladas.",
  },
  {
    icon: Wallet,
    title: "Previdência Privada",
    description: "Estratégias para construção de renda futura com previsibilidade e eficiência fiscal.",
  },
  {
    icon: Shield,
    title: "Seguros e Proteção Patrimonial",
    description: "Proteção do seu plano contra riscos que podem comprometer sua trajetória financeira.",
  },
  {
    icon: Building2,
    title: "Consórcios Estratégicos",
    description: "Ferramenta de alavancagem patrimonial utilizada apenas quando faz sentido dentro da estratégia global.",
  },
  {
    icon: Users,
    title: "Planejamento Sucessório",
    description: "Estruturação para transmissão de patrimônio com eficiência tributária e segurança jurídica para sua família.",
  },
];

const ServicesSection = () => {
  return (
    <section className="bg-[#FAFAFA] py-16 sm:py-20 md:py-24">
      <div className="container-wide">
        {/* Header */}
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <div className="w-12 h-1 bg-primary mb-6 mx-auto" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-[#112711] leading-tight mb-4 sm:mb-6">
            O planejamento é o começo.{" "}
            <span className="text-primary">A estratégia é completa.</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-[#112711]/80 max-w-3xl mx-auto">
            Nosso trabalho começa com um <strong className="text-[#112711] font-semibold">planejamento financeiro profundo</strong>, que traz clareza sobre sua situação atual e seus objetivos de longo prazo.
            A partir dele, estruturamos todas as decisões patrimoniais para que <strong className="text-[#112711] font-semibold">seu plano funcione na prática</strong> — hoje e no futuro.
          </p>
        </AnimatedSection>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {services.map((service, index) => (
            <AnimatedSection key={service.title} delay={index * 0.1}>
              <motion.div
                className="bg-white rounded-2xl p-6 sm:p-8 h-full border border-[#112711]/10 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                whileHover={{ y: -4 }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl font-serif font-medium text-[#112711] mb-3">
                  {service.title}
                </h3>
                <p className="text-sm sm:text-base text-[#112711]/70 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>

        {/* Closing text */}
        <AnimatedSection className="text-center mb-8 sm:mb-10">
          <p className="text-base sm:text-lg md:text-xl text-[#112711]/80 max-w-2xl mx-auto">
            Cada serviço existe para sustentar um único objetivo:{" "}
            <strong className="text-[#112711] font-semibold">garantir que seu plano financeiro funcione e te leve à liberdade com segurança.</strong>
          </p>
        </AnimatedSection>

        {/* CTA */}
        <AnimatedSection className="text-center">
          <Link to="/contato" className="btn-primary rounded-full">
            Descubra qual estratégia faz sentido para você
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default ServicesSection;
